import React, { useState } from 'react';
import { ArrowLeft, Link as LinkIcon, Clock, Calendar, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Avatar from '../components/Avatar';
import EditProfile from './EditProfile';

const ViewProfile = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [showEditProfile, setShowEditProfile] = useState(false);

  // User data (combinando AuthContext + datos adicionales del bootstrap)
  const user = {
    id: authUser?.id,
    name: authUser?.name,
    email: authUser?.email,
    avatarUrl: authUser?.avatarUrl,
    created_at: authUser?.created_at || new Date().toISOString(),
    ...authUser
  };

  // Calcular "Last seen" (mock por ahora)
  const getLastSeen = () => {
    return "1 minute ago";
  };

  // Calcular fecha de membresía
  const getMemberSince = () => {
    if (user.created_at) {
      const date = new Date(user.created_at);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    return "October 20, 2025";
  };

  if (showEditProfile) {
    return <EditProfile user={user} onBack={() => setShowEditProfile(false)} />;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/app')}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Volver al Dashboard</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold mb-12">Profile</h1>

        {/* Profile Card */}
        <div className="flex flex-col items-center text-center space-y-6">
          {/* Avatar */}
          <Avatar 
            name={user.name} 
            avatarUrl={user.avatarUrl}
            size="lg"
            className="w-32 h-32 text-4xl"
          />

          {/* Name */}
          <h2 className="text-3xl font-bold">{user.name}</h2>

          {/* Status Info */}
          <div className="space-y-2 text-gray-400">
            <div className="flex items-center justify-center space-x-2">
              <Clock size={18} />
              <span>Last seen {getLastSeen()}</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Calendar size={18} />
              <span>Member since {getMemberSince()}</span>
            </div>
            {user.phone && (
              <div className="flex items-center justify-center space-x-2">
                <Phone size={18} />
                <span>{user.phone}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-4 pt-6">
            <button
              onClick={() => setShowEditProfile(true)}
              className="px-12 py-3 bg-yellow-400 text-black font-bold rounded-full hover:bg-yellow-300 transition-all text-lg"
            >
              EDIT
            </button>
            <button
              onClick={() => {
                // Copy profile link
                const profileUrl = `${window.location.origin}/profile/${user.id}`;
                navigator.clipboard.writeText(profileUrl);
                alert('Profile link copied to clipboard!');
              }}
              className="p-3 border-2 border-white/20 rounded-full hover:bg-white/5 transition-colors"
              title="Copy profile link"
            >
              <LinkIcon size={24} />
            </button>
          </div>
        </div>

        {/* Profile Details Section */}
        {user.website || user.linkedinUrl || user.profession || user.industry || user.country ? (
          <div className="mt-12 space-y-6">
            {user.website && (
              <div>
                <h3 className="text-sm text-gray-400 mb-2">Website</h3>
                <a 
                  href={user.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-teal-400 transition-colors"
                >
                  {user.website}
                </a>
              </div>
            )}

            {user.linkedinUrl && (
              <div>
                <h3 className="text-sm text-gray-400 mb-2">LinkedIn URL</h3>
                <a 
                  href={user.linkedinUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-teal-400 transition-colors break-all"
                >
                  {user.linkedinUrl}
                </a>
              </div>
            )}

            {user.profession && (
              <div>
                <h3 className="text-sm text-gray-400 mb-2">Profession</h3>
                <p className="text-white">{user.profession}</p>
              </div>
            )}

            {user.industry && (
              <div>
                <h3 className="text-sm text-gray-400 mb-2">Industry</h3>
                <p className="text-white">{user.industry}</p>
              </div>
            )}

            {user.country && (
              <div>
                <h3 className="text-sm text-gray-400 mb-2">Country</h3>
                <p className="text-white">{user.country}</p>
              </div>
            )}
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default ViewProfile;
